<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NotesPanel from './NotesPanel.vue';
import VisitsPanel from './VisitsPanel.vue';
import StepModal from './StepModal.vue';
import AssignModal from './AssignModal.vue';
import SetStatusFlow from './SetStatusFlow.vue';
import MessagesPanel from './MessagesPanel.vue';
import TicketPhotos from './TicketPhotos.vue';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { stepFailure } from '../stepErrors';
import { CARD_CLASS } from '../constants';
import { DEFAULT_MAX_IMAGES, DEFAULT_MAX_IMAGE_MB } from '../photos';
import { TIER_STYLE } from '../helpers';
import {
  STATUS_STYLE,
  SLA_STYLE,
  STEP_DEFS,
  stepsToShow,
  statusBody,
  canReassign,
  setStatusChoices,
  formatTimestamp,
  historyText,
  humanizeCode,
} from '../ticketHelpers';

// Steps that open a modal first; the rest are sent at once.
const MODAL_STEPS = ['resolved', 'cancel', 'reopen', 'hand_back'];
// Steps that live in the notes panel, not as buttons.
const NOTES_STEPS = ['eta', 'tenant_note'];

// The ticket's details and actions. Used by the full page and by the side
// panel on the list, so the step logic lives in one place. `changed` is
// emitted after anything that wrote to the ticket, so the list can refresh.
export default {
  name: 'CoLivingTicketDetail',
  components: {
    LoadingState,
    NotesPanel,
    VisitsPanel,
    StepModal,
    AssignModal,
    SetStatusFlow,
    MessagesPanel,
    TicketPhotos,
  },
  props: {
    ticketId: { type: String, required: true },
    // Narrow layout (the side panel): one column of facts.
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      cardClass: CARD_CLASS,
      setStatusOpen: false,
      statusStyle: STATUS_STYLE,
      slaStyle: SLA_STYLE,
      tierStyle: TIER_STYLE,
      stepDefs: STEP_DEFS,
      loadError: '',
      stepOpen: '',
      stepError: '',
      isStepSaving: false,
      assignOpen: false,
      messages: [],
      messagesError: '',
    };
  },
  computed: {
    ...mapGetters('coLivingTickets', {
      ticket: 'getCurrent',
      uiFlags: 'getUiFlags',
    }),
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    timezone() {
      return (this.settings && this.settings.timezone) || '';
    },
    maxImages() {
      return (this.settings && this.settings.max_images) || DEFAULT_MAX_IMAGES;
    },
    maxImageMb() {
      return (
        (this.settings && this.settings.max_image_mb) || DEFAULT_MAX_IMAGE_MB
      );
    },
    // The server decides: an agent's ticket has no allowed steps, so every
    // write control is hidden for them.
    canEdit() {
      return !!this.ticket && (this.ticket.allowed_steps || []).length > 0;
    },
    buttons() {
      if (!this.ticket) return [];
      return stepsToShow(this.ticket.allowed_steps).filter(
        step => !NOTES_STEPS.includes(step) && step !== 'assign'
      );
    },
    showAssign() {
      return (
        !!this.ticket && (this.ticket.allowed_steps || []).includes('assign')
      );
    },
    showReassign() {
      return canReassign(this.ticket);
    },
    setStatusChoices() {
      return setStatusChoices(this.ticket);
    },
    // `escalated_to` is {id, name, role}; with a role but nobody holding it
    // the name is null, and the line says so.
    escalatedToText() {
      const to = this.ticket.escalated_to || {};
      const who =
        to.name || this.$t('CO_LIVING.TICKETS.DETAIL.ESCALATED_NOBODY');
      return this.ticket.escalated_at
        ? `${who} · ${formatTimestamp(this.ticket.escalated_at, this.timezone)}`
        : who;
    },
    // Urgent tickets only: due, then sent and waiting, then the answer.
    checkbackText() {
      const t = this.ticket;
      if (!t.checkback_due_at && !t.checkback_sent_at) return '';
      if (t.checkback_sent_at) {
        const time = formatTimestamp(t.checkback_sent_at, this.timezone);
        return t.checkback_answer
          ? this.$t('CO_LIVING.TICKETS.DETAIL.CHECKBACK_ANSWERED', {
              time,
              answer: t.checkback_answer,
            })
          : this.$t('CO_LIVING.TICKETS.DETAIL.CHECKBACK_SENT', { time });
      }
      return this.$t('CO_LIVING.TICKETS.DETAIL.CHECKBACK_DUE', {
        time: formatTimestamp(t.checkback_due_at, this.timezone),
      });
    },
    closedReasonText() {
      const reason = this.ticket.closed_reason;
      if (this.ticket.status !== 'CLOSED' || !reason) return '';
      const key = `CO_LIVING.TICKETS.DETAIL.CLOSED_REASONS.${reason.toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : humanizeCode(reason);
    },
  },
  watch: {
    // The side panel switches tickets without remounting.
    ticketId() {
      this.messages = [];
      this.messagesError = '';
      this.stepOpen = '';
      this.setStatusOpen = false;
      this.assignOpen = false;
      this.load();
    },
  },
  mounted() {
    this.fetchSettings();
    this.load();
  },
  methods: {
    ...mapActions('coLivingTickets', [
      'fetchTicket',
      'changeStatus',
      'fetchMessages',
    ]),
    ...mapActions('coLivingConfig', ['fetchSettings']),
    formatTimestamp,
    historyText,
    // Server codes (reasons, history steps) in words; unknown ones are
    // humanized, and text a person typed is shown as it is.
    codeLabel(code) {
      if (!code) return '';
      const key = `CO_LIVING.TICKETS.CODES.${String(code).toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : humanizeCode(code);
    },
    // One history line. A hand-set status names both statuses and keeps the
    // reason the admin gave; everything else is the shared text/label rule.
    historyLine(row) {
      if (row.step === 'set_status') {
        const status = value =>
          this.$te(`CO_LIVING.TICKETS.STATUS.${value}`)
            ? this.$t(`CO_LIVING.TICKETS.STATUS.${value}`)
            : value;
        const head = `${this.codeLabel('set_status')}: ${status(
          row.from_status
        )} → ${status(row.to_status)}`;
        return row.reason ? `${head} — ${row.reason}` : head;
      }
      return historyText(row, this.codeLabel);
    },
    async load() {
      this.loadError = '';
      this.messagesError = '';
      try {
        await this.fetchTicket(this.ticketId);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
        return;
      }
      await this.loadMessages();
    },
    // The messages route is admin only (agents get 403), and a failure here
    // must not hide the ticket, so it has its own try.
    async loadMessages() {
      if (!this.canEdit) return;
      try {
        this.messages = await this.fetchMessages(this.ticketId);
      } catch (err) {
        this.messagesError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.TICKETS.MESSAGES.LOAD_FAILED'
        );
      }
    },
    // The ticket in the store was already replaced by the call that changed
    // it; this re-reads it after a visit call or a refused step.
    async refresh() {
      await this.fetchTicket(this.ticketId);
      await this.loadMessages();
    },
    // The notes and visits panels wrote something: re-read, tell the list.
    onPanelChanged() {
      this.$emit('changed');
      return this.refresh();
    },
    onStep(step) {
      this.stepError = '';
      if (MODAL_STEPS.includes(step)) {
        this.stepOpen = step;
        return undefined;
      }
      return this.runStep(step, {});
    },
    async runStep(step, input) {
      this.isStepSaving = true;
      try {
        await this.changeStatus({
          id: this.ticket.id,
          body: statusBody(step, input),
        });
        useAlert(this.$t('CO_LIVING.TICKETS.STEP_DONE'));
        this.stepOpen = '';
        this.$emit('changed');
        // The step may have queued a message to the tenant or the PIC.
        this.loadMessages();
      } catch (err) {
        this.showStepError(err);
      } finally {
        this.isStepSaving = false;
      }
    },
    showStepError(err) {
      const { code, message } = stepFailure(this, err);
      if (this.stepOpen) {
        this.stepError = message;
      } else {
        useAlert(message);
      }
      // The buttons were stale (another admin moved the ticket): reload them.
      if (code === 'step_not_allowed') this.refresh();
    },
    // Since messages went live, some buttons reach people: say who before the
    // admin clicks. Steps not listed send nothing.
    notifyHint(step) {
      if (step === 'needs_visit' || step === 'resolved') {
        const name =
          (this.ticket.requester && this.ticket.requester.name) ||
          this.$t('CO_LIVING.TICKETS.NOTIFY.THE_TENANT');
        return this.$t('CO_LIVING.TICKETS.NOTIFY.WHATSAPP', { name });
      }
      if (step === 'assign') {
        return this.$t('CO_LIVING.TICKETS.NOTIFY.NEW_PIC');
      }
      if (step === 'hand_back') {
        return this.$t('CO_LIVING.TICKETS.NOTIFY.ADMINS');
      }
      return '';
    },
    onAssignDone(result) {
      if (result === null) {
        this.refresh();
        return;
      }
      useAlert(this.$t('CO_LIVING.TICKETS.ASSIGN.DONE'));
      this.$emit('changed');
    },
  },
};
</script>

<template>
  <div data-testid="co-living-ticket-detail">
    <LoadingState
      v-if="uiFlags.isFetchingTicket && !ticket"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError && !ticket"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>

    <div v-else-if="ticket" class="flex flex-col gap-6">
      <section :class="cardClass">
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="px-2 py-0.5 text-xs font-medium rounded"
            :class="statusStyle[ticket.status]"
            data-testid="co-living-ticket-status"
          >
            {{ $t(`CO_LIVING.TICKETS.STATUS.${ticket.status}`) }}
          </span>
          <span
            v-if="ticket.sla_state"
            class="px-2 py-0.5 text-xs font-medium rounded"
            :class="slaStyle[ticket.sla_state]"
          >
            {{ $t(`CO_LIVING.TICKETS.SLA.${ticket.sla_state.toUpperCase()}`) }}
          </span>
          <span
            class="px-2 py-0.5 text-xs font-medium rounded"
            :class="tierStyle[ticket.sla_tier]"
          >
            {{ ticket.sla_tier }}
          </span>
          <span
            v-if="ticket.escalated"
            class="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
            data-testid="co-living-ticket-escalated"
          >
            {{ $t('CO_LIVING.TICKETS.ESCALATED') }}
          </span>
        </div>

        <p
          v-if="ticket.status === 'NEEDS_ADMIN' && ticket.needs_admin_reason"
          class="mt-3 text-sm text-red-600 dark:text-red-400"
          data-testid="co-living-needs-admin-reason"
        >
          {{ codeLabel(ticket.needs_admin_reason) }}
        </p>

        <dl
          class="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm"
          :class="{ 'md:grid-cols-3': !compact }"
        >
          <div>
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.LOCATION') }}
            </dt>
            <dd class="text-slate-900 dark:text-slate-100">
              {{ ticket.location_label }}
            </dd>
          </div>
          <div>
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.CATEGORY') }}
            </dt>
            <dd class="text-slate-900 dark:text-slate-100">
              {{ ticket.category_name }}
              <template v-if="ticket.emergency">
                · {{ $t('CO_LIVING.CATEGORIES.IMMEDIATE') }}
              </template>
            </dd>
          </div>
          <div>
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.REQUESTER') }}
            </dt>
            <dd
              class="text-slate-900 dark:text-slate-100"
              data-testid="co-living-ticket-requester"
            >
              {{ ticket.requester.name || '-' }}
              <span class="block text-xs text-slate-500">{{
                ticket.requester.phone
              }}</span>
            </dd>
          </div>
          <div>
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.ASSIGNEE') }}
            </dt>
            <dd
              class="text-slate-900 dark:text-slate-100"
              data-testid="co-living-ticket-assignee"
            >
              {{
                ticket.assignee
                  ? ticket.assignee.name
                  : $t('CO_LIVING.TICKETS.UNASSIGNED')
              }}
            </dd>
          </div>
          <div>
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.SOURCE') }}
            </dt>
            <dd class="text-slate-900 dark:text-slate-100">
              {{
                $t(`CO_LIVING.TICKETS.SOURCES.${ticket.source.toUpperCase()}`)
              }}
            </dd>
          </div>
          <div>
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.CREATED') }}
            </dt>
            <dd class="text-slate-900 dark:text-slate-100">
              {{ formatTimestamp(ticket.created_at, timezone) }}
            </dd>
          </div>
          <div v-if="ticket.ack_due_at">
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.ACK_DUE') }}
            </dt>
            <dd class="text-slate-900 dark:text-slate-100">
              {{ formatTimestamp(ticket.ack_due_at, timezone) }}
            </dd>
          </div>
          <div v-if="ticket.eta_due_at">
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.ETA_DUE') }}
            </dt>
            <dd class="text-slate-900 dark:text-slate-100">
              {{ formatTimestamp(ticket.eta_due_at, timezone) }}
            </dd>
          </div>
          <div v-if="ticket.ack_breached_at">
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.ACK_MISSED') }}
            </dt>
            <dd
              class="text-slate-900 dark:text-slate-100"
              data-testid="co-living-ticket-ack-missed"
            >
              {{ formatTimestamp(ticket.ack_breached_at, timezone) }}
            </dd>
          </div>
          <div v-if="ticket.eta_breached_at">
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.ETA_MISSED') }}
            </dt>
            <dd
              class="text-slate-900 dark:text-slate-100"
              data-testid="co-living-ticket-eta-missed"
            >
              {{ formatTimestamp(ticket.eta_breached_at, timezone) }}
            </dd>
          </div>
          <div v-if="ticket.escalated && ticket.escalated_to">
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.ESCALATED_TO') }}
            </dt>
            <dd
              class="text-slate-900 dark:text-slate-100"
              data-testid="co-living-ticket-escalated-to"
            >
              {{ escalatedToText }}
            </dd>
          </div>
          <div v-if="checkbackText">
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.CHECKBACK') }}
            </dt>
            <dd
              class="text-slate-900 dark:text-slate-100"
              data-testid="co-living-ticket-checkback"
            >
              {{ checkbackText }}
            </dd>
          </div>
          <div v-if="closedReasonText">
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.DETAIL.CLOSED_REASON') }}
            </dt>
            <dd
              class="text-slate-900 dark:text-slate-100"
              data-testid="co-living-ticket-closed-reason"
            >
              {{ closedReasonText }}
            </dd>
          </div>
          <div>
            <dt class="text-slate-500">
              {{ $t('CO_LIVING.TICKETS.COLUMNS.DAYS_OPEN') }}
            </dt>
            <dd class="text-slate-900 dark:text-slate-100">
              {{ $t('CO_LIVING.TICKETS.DAYS', { n: ticket.days_open }) }}
            </dd>
          </div>
        </dl>

        <div class="mt-4">
          <p class="text-sm text-slate-500">
            {{ $t('CO_LIVING.TICKETS.DETAIL.DESCRIPTION') }}
          </p>
          <p
            class="mt-1 text-sm whitespace-pre-line text-slate-900 dark:text-slate-100"
            data-testid="co-living-ticket-description"
          >
            {{ ticket.description }}
          </p>
        </div>
      </section>

      <section
        v-if="canEdit"
        class="flex flex-wrap gap-2"
        :class="[cardClass]"
        data-testid="co-living-ticket-actions"
      >
        <woot-button
          v-for="step in buttons"
          :key="step"
          v-tooltip.top="notifyHint(step)"
          :color-scheme="stepDefs[step].danger ? 'alert' : 'secondary'"
          :disabled="isStepSaving"
          :data-testid="`co-living-step-${step}`"
          @click="onStep(step)"
        >
          {{ $t(`CO_LIVING.TICKETS.STEPS.${step.toUpperCase()}`) }}
        </woot-button>
        <woot-button
          v-if="showAssign"
          v-tooltip.top="notifyHint('assign')"
          data-testid="co-living-step-assign"
          @click="assignOpen = true"
        >
          {{ $t('CO_LIVING.TICKETS.STEPS.ASSIGN') }}
        </woot-button>
        <woot-button
          v-if="showReassign"
          v-tooltip.top="notifyHint('assign')"
          color-scheme="secondary"
          data-testid="co-living-step-reassign"
          @click="assignOpen = true"
        >
          {{ $t('CO_LIVING.TICKETS.ASSIGN.REASSIGN') }}
        </woot-button>
        <woot-button
          v-if="
            setStatusChoices.allowed.length || setStatusChoices.blocked.length
          "
          color-scheme="secondary"
          variant="clear"
          data-testid="co-living-set-status-open"
          @click="setStatusOpen = true"
        >
          {{ $t('CO_LIVING.TICKETS.SET_STATUS.TITLE') }}
        </woot-button>
      </section>

      <NotesPanel
        :ticket="ticket"
        :can-edit="
          canEdit &&
          (ticket.allowed_steps.includes('eta') ||
            ticket.allowed_steps.includes('tenant_note'))
        "
        :timezone="timezone"
        @changed="onPanelChanged"
      />

      <VisitsPanel
        :ticket="ticket"
        :can-edit="canEdit"
        @changed="onPanelChanged"
      />

      <section :class="cardClass" data-testid="co-living-photos-card">
        <TicketPhotos
          :ticket="ticket"
          :can-edit="canEdit"
          :max-images="maxImages"
          :max-image-mb="maxImageMb"
          :timezone="timezone"
          @refresh="refresh"
        />
      </section>

      <section :class="cardClass" data-testid="co-living-history">
        <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{ $t('CO_LIVING.TICKETS.HISTORY.TITLE') }}
        </h3>
        <ul class="mt-3 divide-y divide-slate-200 dark:divide-slate-700">
          <li
            v-for="(row, index) in ticket.history"
            :key="`${row.at}-${index}`"
            class="py-2 text-sm"
          >
            <span class="text-slate-500">
              {{ formatTimestamp(row.at, timezone) }}
            </span>
            <span class="ml-2 text-slate-900 dark:text-slate-100">
              {{ historyLine(row) }}
            </span>
            <span class="ml-2 text-xs text-slate-500">
              {{ row.actor_name || row.actor }}
            </span>
          </li>
        </ul>
      </section>

      <section
        v-if="canEdit"
        :class="cardClass"
        data-testid="co-living-messages"
      >
        <div class="flex items-center justify-between gap-2">
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
            {{ $t('CO_LIVING.TICKETS.MESSAGES.TITLE') }}
          </h3>
          <!-- The sender runs every 10 seconds, so a fresh row may be queued. -->
          <woot-button
            variant="clear"
            size="small"
            icon="arrow-clockwise"
            data-testid="co-living-messages-refresh"
            @click="loadMessages"
          >
            {{ $t('CO_LIVING.TICKETS.MESSAGES.REFRESH') }}
          </woot-button>
        </div>
        <MessagesPanel
          :messages="messages"
          :error="messagesError"
          :timezone="timezone"
          :requester="ticket.requester"
        />
      </section>
    </div>

    <StepModal
      v-if="stepOpen"
      :step="stepOpen"
      :error="stepError"
      :is-loading="isStepSaving"
      @close="stepOpen = ''"
      @confirm="runStep(stepOpen, $event)"
    />
    <AssignModal
      v-if="assignOpen"
      :ticket="ticket"
      @close="assignOpen = false"
      @done="onAssignDone"
    />
    <SetStatusFlow
      v-if="setStatusOpen"
      :ticket-id="ticket.id"
      :choices="setStatusChoices"
      @close="setStatusOpen = false"
      @changed="$emit('changed')"
    />
  </div>
</template>
